import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import { getAllNews, NEWS_CATEGORIES } from "@/lib/media";
import NewsCard from "@/components/base/NewsCard";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";

const PAGE_SIZE = 5;

export default function NewsListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") ?? "全部";

  const allNews = useMemo(() => getAllNews(), []);
  const filtered = useMemo(
    () =>
      activeCategory === "全部"
        ? allNews
        : allNews.filter((item) => item.category === activeCategory),
    [allNews, activeCategory],
  );

  const pageParam = Number(searchParams.get("page") ?? "1");
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(Math.max(1, Number.isNaN(pageParam) ? 1 : pageParam), totalPages);
  const pagedList = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  usePageMeta({
    title: activeCategory === "全部" ? "影视资讯 - 西瓜影音" : `${activeCategory} - 影视资讯 - 西瓜影音`,
    description:
      "西瓜影音影视资讯频道，聚合最新影视新闻、行业动态、深度影评与预告片资讯，帮你了解影视圈的最新动向。",
    keywords: "影视资讯,影视新闻,行业动态,影评,预告片,西瓜影音",
  });

  const changeCategory = (category: string) => {
    const next = new URLSearchParams();
    if (category !== "全部") next.set("category", category);
    setSearchParams(next);
  };

  const changePage = (page: number) => {
    const next = new URLSearchParams(searchParams);
    if (page <= 1) next.delete("page");
    else next.set("page", String(page));
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-10 animate-fade-in">
      <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-5" aria-label="面包屑导航">
        <Link to="/" className="hover:text-primary-400 transition-colors">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-200">影视资讯</span>
      </nav>

      <header className="rounded-lg bg-gradient-to-r from-background-900 to-primary-950 border border-background-800 p-6 md:p-8 mb-6">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50 mb-3">
          影视资讯
        </h1>
        <p className="text-sm md:text-base text-foreground-300 leading-relaxed max-w-3xl">
          聚合最新影视新闻、行业观察、深度影评与预告片资讯，第一时间掌握影视圈动态。
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        {NEWS_CATEGORIES.map((category) => {
          const active = activeCategory === category;
          return (
            <button
              key={category}
              type="button"
              onClick={() => changeCategory(category)}
              className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap cursor-pointer transition-colors ${
                active
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-800 text-foreground-300 hover:bg-background-700 hover:text-foreground-100"
              }`}
            >
              {category}
            </button>
          );
        })}
      </div>

      {pagedList.length > 0 ? (
        <>
          <div className="space-y-4">
            {pagedList.map((item) => (
              <NewsCard key={item.id} item={item} />
            ))}
          </div>
          <Pagination page={currentPage} totalPages={totalPages} onChange={changePage} />
        </>
      ) : (
        <EmptyState
          icon="ri-newspaper-line"
          title="该分类下暂无资讯"
          description="换个分类看看吧，或者返回全部资讯浏览最新内容。"
          onReset={() => changeCategory("全部")}
          resetLabel="查看全部资讯"
        />
      )}
    </div>
  );
}